import React, { useRef, useState, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { useIntl, getLocale } from 'umi';
import { SelectMultiple, SelectMultipleProps, Radio } from 'tea-component';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { getSearcherFilter, getTextWithLocale } from '../utils';
import { HOT_COUNTRIES_PREFIX, HOT_COUNTRIES } from '../constants';

import type {
  IBaseProps,
  IRegionCountry,
  ISelectOptionWithGroup,
} from '@/pages/intelligence/types/common';
import { useDeepCompareEffect } from 'ahooks';

const removeHotPrefix = (countryCode: string): string => {
  const reg = new RegExp(`${HOT_COUNTRIES_PREFIX}$`);
  return countryCode?.replace(reg, '') ?? '';
};

const removeHotCountries = (countries: string[]): string[] => {
  if (!Array.isArray(countries)) return [];
  return countries.filter((item) => item && !item?.endsWith(HOT_COUNTRIES_PREFIX));
};

const synchronizeHotCountries = (next: string[], prev: string[], hots: string[]): string[] => {
  if (!Array.isArray(next)) return [];
  if (!Array.isArray(prev)) return next;
  const result = [...next];
  const isAdd = next.length > prev.length;
  next.forEach((item, idx) => {
    const itemWithHotPrefix = item + HOT_COUNTRIES_PREFIX;
    const itemWithoutHotPrefix = removeHotPrefix(item);
    const flag1 = hots.includes(item);
    const flag2 = hots.includes(itemWithHotPrefix);
    // 非热度国家 不做任何处理
    if (!flag1 && !flag2) return;
    // 有带hot后缀的，没有不带hot后缀的
    if (flag1 && !next?.includes(itemWithoutHotPrefix)) {
      if (isAdd) {
        result.push(itemWithoutHotPrefix);
      } else {
        result.splice(idx, 1);
      }
    }
    // 有不带hot后缀的，没有带hot后缀的
    if (flag2 && !next?.includes(itemWithHotPrefix)) {
      if (isAdd) {
        result.push(itemWithHotPrefix);
      } else {
        result.splice(idx, 1);
      }
    }
  });
  return result;
};

interface IMultipleCountrySelectProps extends IBaseProps, SelectMultipleProps {
  title?: string;
  // 模式搜索时，是否同时也匹配code进行搜索
  codeSearchable?: boolean;
  hotCountries?: IRegionCountry[];
  renderOptionName?: (name: string, code: string) => string | React.ReactElement;
  // 多选时，All选项按钮 右侧的info icon提示内容，需要传翻译用的key
  allTipContext?: string;
  options: ISelectOptionWithGroup[];
  onChange?: (payload: string[], context: any) => void;
  onConfirm: (payload: string[]) => void;
}

const MultipleCountrySelect: React.FC<IMultipleCountrySelectProps> = (props) => {
  const {
    style,
    className,
    title = 'dbi_country',
    codeSearchable = true,
    disabled = false,
    hotCountries: hotCountriesOuter = HOT_COUNTRIES,
    renderOptionName,
    groups: groupsOuter,
    options: optionsOuter,
    value: valueOuter,
    onChange,
    onConfirm,
    onSearch,
    onClose,
    isAll = true,
    allTipContext = '',
  } = props;

  // console.log('MultipleCountrySelect', props);

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isChanged = useRef(false);

  const filter = getSearcherFilter(codeSearchable);

  const valuesOuter = optionsOuter?.map((item) => item?.value) ?? [];
  // const hotCountries =
  //   hotCountriesOuter?.filter((item) =>
  //     valuesOuter?.includes(removeHotPrefix(item?.country_code)),
  //   ) ?? [];
  const hotCountries =
    optionsOuter?.length > 2
      ? hotCountriesOuter?.filter((item) =>
          valuesOuter?.includes(removeHotPrefix(item?.country_code)),
        ) ?? []
      : [];
  const hotCountriesValues = hotCountries?.map((item) => item?.country_code) ?? [];
  const hotCountriesOptions = hotCountries?.map((item) => {
    const {
      country_code: countryCode,
      country_name_en: countryNameEN,
      country_name_ch: countryNameCH,
    } = item ?? {};
    const countryName = getTextWithLocale(countryNameEN, countryNameCH, locale);
    return {
      groupKey: HOT_COUNTRIES_PREFIX,
      value: countryCode,
      label: countryName,
      text: (
        <div className="dbi-filter-selector-option-node">
          <img
            className="dbi-filter-selector-option-node-icon"
            src={getCountryFlag(removeHotPrefix(countryCode))}
            alt=""
          />
          <span
            className="dbi-filter-selector-option-node-text"
            // @ts-ignore
            dangerouslySetInnerHTML={{
              __html: renderOptionName?.(countryName as string, countryCode) ?? countryName,
            }}
          />
        </div>
      ),
    };
  });
  const groups = hotCountries?.length
    ? { ...groupsOuter, [HOT_COUNTRIES_PREFIX]: formatMessage({ id: 'dbi_hot' }) }
    : groupsOuter;
  const options = hotCountries?.length
    ? [...hotCountriesOptions, ...(optionsOuter ?? [])]
    : optionsOuter;

  const [keyword, setKeyword] = useState('');
  const [value, setValue] = useState<string[]>([]);

  const isNoData = !(options && options?.length > 0);
  const isNoSearchData = !(
    options && options?.filter((option) => filter(keyword, option))?.length > 0
  );

  const handleChange = (payload: string[], context: any) => {
    const synchronizedCountries = synchronizeHotCountries(payload, value, hotCountriesValues);
    const countriesWithoutHot = removeHotCountries(payload);
    setValue(synchronizedCountries);
    onChange?.(countriesWithoutHot, context);
    isChanged.current = true;
  };

  const handleSearch = (keyword?: string, context?: any) => {
    const keywordTmp = keyword ?? '';
    setKeyword(keywordTmp);
    onSearch?.(keywordTmp, context);
  };

  const handleClose = () => {
    if (isChanged.current) {
      let valueTmp = value;
      const valueTmpCur = removeHotCountries(value);
      if (!valueTmpCur?.length) {
        valueTmp = options?.map((option) => option?.value) ?? [];
      }
      valueTmp = removeHotCountries(valueTmp);
      onConfirm(valueTmp);
    }
    onClose?.();
    setKeyword('');
    isChanged.current = false;
  };

  useEffect(() => {
    const valueTmp = Array.isArray(valueOuter) ? valueOuter : [];
    const hotCountriesValuesTmp = HOT_COUNTRIES?.map((item) => item?.country_code)?.filter((item) =>
      valueTmp?.includes(removeHotPrefix(item)),
    );
    const valueWithHotTmp = [...hotCountriesValuesTmp, ...valueTmp];
    setValue(valueWithHotTmp);
  }, [valueOuter]);

  useDeepCompareEffect(() => {
    if (isAll) {
      const valueTmp = optionsOuter?.map((option) => option?.value);
      const hotCountriesValuesTmp = HOT_COUNTRIES?.map((item) => item?.country_code)?.filter(
        (item) => valueTmp?.includes(removeHotPrefix(item)),
      );
      const valueWithHotTmp = [...hotCountriesValuesTmp, ...(valueTmp || [])];
      setValue(valueWithHotTmp);
    }
    isChanged.current = true;
  }, [isAll, optionsOuter]);

  const isSelectAll = useMemo(() => {
    const optionsValus = options?.map((item: any) => item.value);
    return value?.length > 0 && optionsValus?.every((a) => value?.includes(a));
  }, [value, options]);
  const handleToggleSelectAll = () => {
    const isSelectAllTmp = !isSelectAll;
    const valueTmp = !isSelectAllTmp ? [] : options?.map((option) => option?.value);
    isChanged.current = true;
    setValue(valueTmp ?? []);
  };

  const buttonUI = (() => {
    const valueUI = (() => {
      if (!value?.length) return '';
      if (value?.length === 1) {
        // return options?.find((item) => item.value === value[0])?.label || '';
        return options?.find((item) => item.value === value[0])?.text || '';
      }
      if (value?.length === 2 && value.some((item) => item.endsWith('_hot'))) {
        // return options?.find((item) => item.value === value[0])?.label || '';
        const valueList = value?.filter((item) => !item.endsWith('_hot')) || [];
        return options?.find((item) => item.value === valueList[0])?.text || '';
      }
      const optionsValus = options?.map((item: any) => item.value);
      if (value?.length > 0 && optionsValus?.every((a) => value?.includes(a))) {
        return <>{formatMessage({ id: 'dbi_all' })}</>;
      }
      return removeHotCountries(value)?.length;
    })();

    return (
      <>
        <div className="select-button">
          <div className="select-button-text">{formatMessage({ id: title })}</div>
          {valueUI}
        </div>
      </>
    );
  })();

  return (
    <SelectMultiple
      style={style}
      className={classnames('dbi-composed-multiple-country-select', className)}
      overlayClassName={classnames(
        'dbi-composed-country-select-overlay dbi-composed-multiple-country-select-overlay',
        {
          'is-no-data': isNoData,
          'is-no-search-data': isNoSearchData,
        },
      )}
      placeholder={formatMessage({ id: 'dbi_please_select' })}
      appearance="button"
      listWidth={style?.width ?? 230}
      listHeight={300}
      disabled={disabled}
      staging={false}
      searchable={!isNoData}
      searchPlaceholder={formatMessage({ id: 'dbi_label_search' })}
      filter={filter}
      button={buttonUI}
      groups={groups}
      options={options}
      value={value}
      onChange={handleChange}
      onSearch={handleSearch}
      onClose={handleClose}
      tips={
        isNoSearchData ? (
          <div className="dbi-market-selector-no-data">
            <span>{formatMessage({ id: 'dbi_no_data' })}</span>
          </div>
        ) : (
          <Radio
            className="dbi-market-selector-radio"
            value={isSelectAll}
            onChange={handleToggleSelectAll}
          >
            <div className="dbi-market-selector-radio-top-all">
              <span className="dbi-market-selector-radio-top-all-text">
                {formatMessage({ id: 'dbi_all_select' })}
              </span>
              {allTipContext && <TipContext tipText={formatMessage({ id: allTipContext })} />}
            </div>
          </Radio>
        )
      }
    />
  );
};

export default MultipleCountrySelect;
